import { useState } from 'react'
import type { AttributeDefinition, SessionCommand } from '@fitting-room/contracts'
import { sortedOptions } from '../lib/attributes'
import { uiStrings } from '../lib/strings'
import { text } from '../lib/text'

/**
 * Filter chips, built entirely from `filterableAttributes` on the step crossed
 * with the tenant's own attribute definitions. Adding a filterable attribute
 * is a data change; this file does not change.
 */
export function FilterChips({
  attributes,
  active,
  setAside,
  locale,
  disabled,
  onChange,
}: {
  readonly attributes: readonly AttributeDefinition[]
  readonly active: Readonly<Record<string, readonly string[]>>
  /**
   * Attribute keys she has filtered on that THIS ranking could not honour.
   *
   * Her selection stays — it is still her answer, and it comes back into force
   * the moment the deck can meet it, so clearing the chip would be throwing
   * away something she said. What changes is that the chip stops claiming to
   * be in force while the gowns behind it say otherwise.
   */
  readonly setAside?: readonly string[]
  readonly locale: string
  readonly disabled: boolean
  readonly onChange: (command: SessionCommand) => void
}) {
  const t = uiStrings(locale)
  const [open, setOpen] = useState<string | null>(null)
  const activeCount = attributes.reduce((n, def) => n + (active[def.key]?.length ?? 0), 0)

  if (attributes.length === 0) return null

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 overflow-x-auto pb-1" role="group" aria-label={t.filters}>
        {attributes.map((def) => {
          const selected = active[def.key] ?? []
          const isOpen = open === def.key
          const ignored = selected.length > 0 && (setAside?.includes(def.key) ?? false)
          return (
            <button
              key={def.key}
              type="button"
              className="shrink-0 border px-3 py-1.5 text-[0.8125rem] tracking-wide whitespace-nowrap"
              aria-expanded={isOpen}
              data-set-aside={ignored ? true : undefined}
              style={{
                // Dashed and dimmed while it is not in force: the same way the
                // questionnaire draws an option that means "no preference", so
                // "on but not applied" reads as a state rather than as damage.
                borderStyle: ignored ? 'dashed' : 'solid',
                borderColor: ignored ? 'var(--fr-rule)' : selected.length > 0 ? 'var(--fr-primary)' : 'var(--fr-rule)',
                color: ignored ? 'var(--fr-ink-faint)' : selected.length > 0 ? 'var(--fr-primary)' : 'var(--fr-ink-dim)',
                background: isOpen ? 'var(--fr-raised)' : 'transparent',
                borderRadius: 'var(--fr-radius)',
              }}
              onClick={() => setOpen(isOpen ? null : def.key)}
            >
              {text(def.label, locale)}
              {selected.length > 0 ? ` · ${selected.length}` : ''}
              {ignored ? <span className="ml-1 opacity-80">({t.filterSetAside})</span> : null}
            </button>
          )
        })}
        {activeCount > 0 ? (
          <button
            type="button"
            className="fr-btn-quiet shrink-0 whitespace-nowrap"
            disabled={disabled}
            onClick={() => {
              for (const def of attributes) {
                if ((active[def.key]?.length ?? 0) > 0) onChange({ type: 'set_filter', attributeKey: def.key, values: [] })
              }
              setOpen(null)
            }}
          >
            {t.clear}
          </button>
        ) : null}
      </div>

      {attributes.map((def) => {
        if (open !== def.key) return null
        const selected = active[def.key] ?? []
        return (
          <div key={def.key} className="mt-2 flex flex-wrap gap-2 border p-3" style={{ borderColor: 'var(--fr-rule)' }}>
            {sortedOptions(def).map((option) => {
              const isOn = selected.includes(option.value)
              return (
                <button
                  key={option.value}
                  type="button"
                  className="border px-3 py-1.5 text-[0.8125rem]"
                  aria-pressed={isOn}
                  disabled={disabled}
                  style={{
                    borderColor: isOn ? 'var(--fr-primary)' : 'var(--fr-rule)',
                    background: isOn ? 'color-mix(in srgb, var(--fr-primary) 18%, transparent)' : 'transparent',
                    color: isOn ? 'var(--fr-ink)' : 'var(--fr-ink-dim)',
                    borderRadius: 'var(--fr-radius)',
                  }}
                  onClick={() => {
                    const values = isOn ? selected.filter((v) => v !== option.value) : [...selected, option.value]
                    onChange({ type: 'set_filter', attributeKey: def.key, values })
                  }}
                >
                  {text(option.label, locale)}
                </button>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}
